"use client";

import {
  useStytchB2BClient,
  useStytchMember,
  useStytchMemberSession,
} from "@stytch/nextjs/b2b";
import type { Member as StytchB2BUser } from "@stytch/vanilla-js/b2b";
import { jwtDecode } from "jwt-decode";
import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { SWRConfig, mutate } from "swr";
// @ts-ignore
import useLocalStorageState from "use-local-storage-state";
import { AUTH_CONFIG } from "../auth/config";
import { SHORT_SESSION_DURATION_MINUTES, SHORT_SESSION_ORG_IDS } from "../env";
import ErrorPage from "../errors/error-page";
import { profileURL, useProfile } from "../hooks/use-profile";
import { isJWTExpired } from "../hooks/use-stytch-jwt";
import type { Profile, ProfileOrg, ProfileProject } from "../models";
import { sortProjects } from "../utils/project-utils";
import {
  identifyOrg,
  identifyUser,
  usePostHogUserIdentity,
} from "./posthog/posthog-client-provider";
import { useLayoutPreferences } from "./preferences-provider";

export const UserContext = createContext<UserContextValue>({
  isReady: false,
  isStytchInitialized: false,
  isProfileLoading: false,
  timeoutError: undefined,
  buildRequestHeaders: async () => ({}),
  logout: async () => {
    posthog.reset();
    return await Promise.resolve();
  },
  setOrgIdFromJWT: () => {
    throw new Error("setOrgIdFromJWT must be used within a UserProvider");
  },
  setPauseFetchProfile: () => {
    throw new Error("setPauseFetchProfile must be used within a UserProvider");
  },
  isVercelManagedOrg: false,
  canShowAuthenticatedUI: false,
});

export const useUser = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export interface UserContextValue {
  timeoutError: Error | undefined;
  isReady: boolean;
  isProfileLoading: boolean;
  isStytchInitialized: boolean;
  stytchUser?: StytchB2BUser;
  isStytchUserInitialized?: boolean;
  profile?: Profile;
  currentOrg?: string;
  currentOrgFull?: ProfileOrg;
  currentProject?: string;
  currentProjectFull?: ProfileProject;
  isGroqUser?: boolean;
  changeOrg?: (orgId: string) => Promise<void>;
  changeProject?: (projectId: string) => void;
  setOrgIdFromJWT: () => void;
  buildRequestHeaders: (options?: {
    noContentType?: boolean;
  }) => Promise<Record<string, string>>;
  logout: (options?: { redirect?: boolean }) => Promise<void>;
  setPauseFetchProfile: (pause: boolean) => void;
  isVercelManagedOrg: boolean;
  sanitizedUserName?: string;

  /**
   * Returns whether we have successfully fetched a stytch user object for the user or
   * whether our server confirmed that the user is authenticated.
   */
  canShowAuthenticatedUI: boolean;
}

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const stytch = useStytchB2BClient();
  const { isInitialized } = useStytchMemberSession();
  const { member: user, isInitialized: isStytchUserInitialized } =
    useStytchMember();
  const [timeoutError, setTimeoutError] = useState<Error | undefined>(
    undefined,
  );

  // we know the user will be authenticated from this flag
  const postHogUserIdentity = usePostHogUserIdentity();
  // once our stytch user is initialized, we can use our frontend state instead of
  // relying on the server side posthog evaluation
  const canShowAuthenticatedUI = isStytchUserInitialized
    ? user != null
    : postHogUserIdentity?.isIdentified === true;

  const { getLayoutPreference, setLayoutPreference, clearLayoutPreferences } =
    useLayoutPreferences();

  let sessionJwt: string | undefined;
  if (typeof window !== "undefined" && isInitialized && user) {
    sessionJwt = stytch.session.getTokens()?.session_jwt;
  }

  // during auth/signup, we pause the profile fetch until the profile is created.
  const [pauseFetchProfileSince, setPauseFetchProfileSince] =
    useLocalStorageState("pause-fetch-profile-since-ts", {
      defaultValue: 0,
    });

  // if for some reason the local storage ts is not cleared, this will clear it after 5 seconds
  const pausedFetchProfile = Date.now() - pauseFetchProfileSince < 5_000;
  const setPauseFetchProfile = (pause: boolean) => {
    if (pause) {
      setPauseFetchProfileSince(Date.now());
    } else {
      setPauseFetchProfileSince(0);
    }
  };

  const {
    data,
    error,
    isLoading: isProfileLoading,
  } = useProfile(true, pausedFetchProfile ? undefined : sessionJwt);
  const { profile, errorCode, status } = data || {
    profile: null,
    unauthenticatedError: false,
  };
  const router = useRouter();
  const logout = async ({ redirect = true }: { redirect?: boolean } = {}) => {
    await stytch.session.revoke();
    if (redirect) {
      router.push("/");
    }
  };

  const defaultOrg = profile?.user.orgs.data?.[0]?.id || "";
  const [currentOrg, setCurrentOrg] = useState<string>(
    (getLayoutPreference("current-org") as string) || defaultOrg,
  );

  const currentOrgFull = profile?.user?.orgs?.data.find(
    (org) => org.id === currentOrg,
  );

  const sortedProjects = sortProjects(currentOrgFull?.projects.data || []);
  const defaultProject = sortedProjects[0]?.id || "";

  const [currentProject, setCurrentProject] = useState<string>(
    (getLayoutPreference("current-project") as string) || defaultProject,
  );

  const currentProjectFull = currentOrgFull?.projects?.data.find(
    (p) => p.id === currentProject,
  );

  useEffect(() => {
    if (currentOrgFull) {
      const projectIsValid = currentOrgFull.projects.data.find(
        (project) => project.id === currentProject,
      );
      if (!projectIsValid) {
        const firstProject = sortedProjects[0]?.id || "";
        setCurrentProject(firstProject);
        if (firstProject) {
          setLayoutPreference("current-project", firstProject);
        } else {
          clearLayoutPreferences("current-project");
        }
      }
    }
  }, [currentOrgFull]);

  const isVercelManagedOrg = currentOrgFull?.vercel_managed === true;

  // Create sanitized org name from user's name or email
  const sanitizedUserName = profile?.user
    ? (() => {
        const targetName =
          String(profile.user.name).trim() || profile.user.email;
        return targetName
          .replace(/[^a-zA-Z0-9\s_-]/g, "-")
          .replace(/\s+/g, " ")
          .trim();
      })()
    : undefined;

  const changeOrg = async (orgId: string) => {
    // batch all of these state changes
    setCurrentOrg(orgId);
    // clear out current project when changing org
    setCurrentProject("");
    setLayoutPreference("current-org", orgId);
    await stytch.session.exchange({
      organization_id: orgId,
      session_duration_minutes: AUTH_CONFIG.sessionDurationMinutes,
    });
    await mutate(profileURL, undefined, { revalidate: true });
  };

  const setOrgIdFromJWT = () => {
    const jwt = window.document.cookie
      .split("; ")
      .find((row) => row.startsWith("stytch_session_jwt="))
      ?.split("=")[1];
    if (jwt) {
      const decoded = jwtDecode<{
        "https://stytch.com/organization": { slug: string };
      }>(jwt);
      const orgId = decoded?.["https://stytch.com/organization"]?.slug;
      if (orgId) {
        setCurrentOrg(orgId);
        setCurrentProject("");
        setLayoutPreference("current-org", orgId);
      }
    }
  };

  const changeProject = (projectId: string) => {
    setCurrentProject(projectId);
    setLayoutPreference("current-project", projectId);
  };

  const buildRequestHeaders = async (opts?: {
    noContentType?: boolean;
  }) => {
    let sessionJwt = stytch.session.getTokens()?.session_jwt;

    if (!sessionJwt || isJWTExpired(sessionJwt)) {
      await stytch.session.authenticate();
      sessionJwt = stytch.session.getTokens()?.session_jwt;
    }

    const result: { [key: string]: string } = {
      authorization: `Bearer ${sessionJwt}`,
      "groq-organization": currentOrg || "",
      // "groq-project": currentProject || "",
    };

    if (!opts?.noContentType) {
      result["Content-Type"] = "application/json";
    }

    return result;
  };

  useEffect(() => {
    // If the JWT is expired, it will be auto refreshed by stytch, do nothing
    if (errorCode === "invalid_or_expired_jwt") {
      // otherwise, the jwt is busted, log the user out.
    } else if (status === 401) {
      logout();
    }
  }, [errorCode, status]);

  const isOrgValid = (): boolean => {
    if (!profile) {
      return false;
    }
    return !!profile.user.orgs.data.find((org) => org.id === currentOrg);
  };
  useEffect(() => {
    if (profile) {
      const defaultToFirstOrg = () => {
        if (profile.user.orgs.data[0]) {
          setCurrentOrg(profile.user.orgs.data[0].id);
          setLayoutPreference("current-org", profile.user.orgs.data[0].id);
        }
      };

      if (currentOrg) {
        // Check the current org is still valid
        const org = profile.user.orgs.data.find((org) => org.id === currentOrg);
        if (!org) {
          defaultToFirstOrg();
        }
      } else {
        defaultToFirstOrg();
      }
    }
  }, [profile]);

  useEffect(() => {
    if (!(profile && currentOrg)) {
      return;
    }
    const payload = {
      organization_id: currentOrg,
      user_id: profile?.user.id,
    };
    // @ts-ignore
    if (typeof window !== "undefined" && window.gtag) {
      try {
        // @ts-ignore
        window.gtag("set", payload);
      } catch {
        // Silently handle gtag errors (likely due to ad blockers)
      }
    }

    identifyUser(profile);
  }, [currentOrg, profile]);

  // B2B users have a single email_address field
  const isGroqUser = user?.email_address?.endsWith("@groq.com");

  const enforceSessionDuration = ({ currentOrg }: { currentOrg: string }) => {
    if (currentOrg && SHORT_SESSION_ORG_IDS.includes(currentOrg)) {
      if (stytch.session.getInfo().session?.expires_at) {
        const expiresAt = new Date(
          stytch.session.getInfo().session?.expires_at || "",
        );
        const now = new Date();
        const diff = expiresAt.getTime() - now.getTime();
        const diffMinutes = diff / (1000 * 60);
        if (diffMinutes > SHORT_SESSION_DURATION_MINUTES) {
          stytch.session.authenticate({
            session_duration_minutes: SHORT_SESSION_DURATION_MINUTES,
          });
        }
      }
    }
  };

  useEffect(() => {
    if (isInitialized && profile) {
      enforceSessionDuration({ currentOrg });
    }
  }, [isInitialized, currentOrg, profile]);

  useEffect(() => {
    // if the profile is still loading after 60 seconds, set a timeout error
    if (isProfileLoading) {
      const timeout = setTimeout(() => {
        setTimeoutError(
          new Error(
            "We ran into an error loading your profile. Please refresh the page.",
          ),
        );
      }, 60_000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isProfileLoading]);

  const isReady =
    !!profile &&
    !!currentOrg &&
    isOrgValid() &&
    !!stytch.session.getTokens()?.session_jwt &&
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    !isJWTExpired(stytch.session.getTokens()?.session_jwt!);

  useEffect(() => {
    if (isReady && currentOrg && currentOrgFull) {
      identifyOrg(currentOrg, {
        name: currentOrgFull?.name || "",
        plan: currentOrgFull?.billing_plan || "",
      });
    }
  }, [isReady, currentOrg, currentOrgFull]);

  return (
    <SWRConfig value={{ revalidateOnFocus: false }}>
      <UserContext.Provider
        value={{
          isStytchInitialized: isInitialized,
          isReady,
          canShowAuthenticatedUI,

          stytchUser: user as StytchB2BUser,
          isStytchUserInitialized,
          profile: profile || undefined,
          isProfileLoading,
          timeoutError,
          isGroqUser,
          currentOrg,
          currentOrgFull,
          currentProject,
          currentProjectFull,
          changeOrg,
          setOrgIdFromJWT,
          buildRequestHeaders,
          logout,
          changeProject,
          setPauseFetchProfile,
          isVercelManagedOrg,
          sanitizedUserName,
        }}
      >
        {error && <ErrorPage error={error} />}
        {!error && children}
      </UserContext.Provider>
    </SWRConfig>
  );
};
